import React, { Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { LoadingSpinner } from '@tw/tw-component-library';
import { AuthenticatedTemplate } from '@azure/msal-react';
import { isAuthBypassed } from '@/authConfig';
import { LandingPage } from '@/components/pages/landingPage/LandingPage';
import { SignalRTestPage } from '@/components/pages/signalRTestPage/SignalRTestPage';
import { WorkspacePage } from '@/components/pages/workspacePage/WorkspacePage';
import { AskHub } from '@/components/pages/askHub/AskHub';
import { AskSession } from '@/components/pages/ask/AskSession';
import { WorkspaceDashboard } from '@/components/pages/workspaceDashboard/WorkspaceDashboard';
import { DocumentsPage } from '@/components/pages/documents/DocumentsPage';
import { ActivitiesPage } from '../pages/activities/ActivitiesPage';
import { PromptLibrary } from '../pages/promptLibrary/PromptLibrary';
import { Anylitics } from '../pages/anylitics/Anylitics';
import { WorkspaceRouteGuard } from './WorkspaceRouteGuard';

interface AppRoutesProps {
  isAuthenticated: boolean;
  isInitializing: boolean;
}

/**
 * Wrapper component for route authentication
 */
const AuthenticatedRoute: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  if (isAuthBypassed) {
    return <>{children}</>;
  }
  return <AuthenticatedTemplate>{children}</AuthenticatedTemplate>;
};

/**
 * Wrapper component for Suspense with loading spinner
 */
const SuspenseWrapper: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <Suspense fallback={<LoadingSpinner variant="fullPage" />}>
    {children}
  </Suspense>
);

/**
 * Wrapper component that handles authentication state during initialization
 * This ensures Routes stay mounted so React Router can preserve the current route
 * Always returns a valid React element to ensure route matching works correctly
 */
const AuthWrapper: React.FC<{
  isAuthenticated: boolean;
  isInitializing: boolean;
  children: React.ReactNode;
}> = ({ isAuthenticated, isInitializing, children }) => {
  if (isInitializing) {
    return <LoadingSpinner variant="fullPage" />;
  }

  if (!isAuthenticated) {
    // Return empty fragment instead of null to ensure route still matches
    // This prevents the catch-all route from redirecting during auth check
    return <></>;
  }

  return <>{children}</>;
};

/**
 * Main application routes component
 * Extracted from App.tsx for easier maintenance
 */
export const AppRoutes: React.FC<AppRoutesProps> = ({
  isAuthenticated,
  isInitializing,
}) => {
  // Always render Routes to preserve current route during initialization
  // Handle authentication state inside route elements
  return (
    <Routes>
      <Route
        path="/"
        element={
          <AuthWrapper
            isAuthenticated={isAuthenticated}
            isInitializing={isInitializing}
          >
            <AuthenticatedRoute>
              <SuspenseWrapper>
                <LandingPage />
              </SuspenseWrapper>
            </AuthenticatedRoute>
          </AuthWrapper>
        }
      />
      <Route
        path="/workspace-dashboard"
        element={
          <AuthWrapper
            isAuthenticated={isAuthenticated}
            isInitializing={isInitializing}
          >
            <AuthenticatedRoute>
              <SuspenseWrapper>
                <WorkspaceDashboard />
              </SuspenseWrapper>
            </AuthenticatedRoute>
          </AuthWrapper>
        }
      />
      <Route
        path="/anylitics"
        element={
          <AuthWrapper
            isAuthenticated={isAuthenticated}
            isInitializing={isInitializing}
          >
            <AuthenticatedRoute>
              <SuspenseWrapper>
                <Anylitics />
              </SuspenseWrapper>
            </AuthenticatedRoute>
          </AuthWrapper>
        }
      />
      <Route
        path="/signalr-test"
        element={
          <AuthWrapper
            isAuthenticated={isAuthenticated}
            isInitializing={isInitializing}
          >
            <AuthenticatedRoute>
              <SignalRTestPage />
            </AuthenticatedRoute>
          </AuthWrapper>
        }
      />
      {/* Single workspace layout: guard stays mounted for all /workspace/:id/* so isOnline and SignalR group join are stable when navigating between workspace pages. */}
      <Route
        path="/workspace/:id"
        element={
          <AuthWrapper
            isAuthenticated={isAuthenticated}
            isInitializing={isInitializing}
          >
            <AuthenticatedRoute>
              <WorkspaceRouteGuard />
            </AuthenticatedRoute>
          </AuthWrapper>
        }
      >
        <Route index element={<WorkspacePage />} />
        <Route path="activities" element={<ActivitiesPage />} />
        <Route path="ask-hub" element={<AskHub />} />
        <Route path="ask-session/:sessionId" element={<AskSession />} />
        <Route path="ask-session" element={<AskSession />} />
        <Route path="documents" element={<DocumentsPage />} />
        <Route path="prompt-library" element={<PromptLibrary />} />
      </Route>
      <Route
        path="*"
        element={
          isInitializing ? (
            <LoadingSpinner variant="fullPage" />
          ) : !isAuthenticated ? null : (
            <Navigate to="/" replace />
          )
        }
      />
    </Routes>
  );
};
